import React from "react";
import { css } from "@emotion/react";
import { COLORS } from "./colors";

const TYPOGRAPHY = {
  en: {
    primary: "'NeueFrutigerWorld-medium'",
    secondary: "Inter Numeric, Source Sans Pro, sans-serif",
  },
  ka: {
    primary: "'NeueFrutigerWorld-medium'",
    secondary: "Inter Numeric, Noto Serif Georgian, sans-serif",
  },
  ru: {
    primary: "'NeueFrutigerWorld-medium'",
    secondary: "Inter Numeric, Source Sans Pro, sans-serif",
  },
};

enum FontFamily {
  Primary = "Primary",
  Secondary = "Secondary",
}

enum TextStyle {
  Regular = 400,
  Medium = 500,
  SemiBold = 600,
  Thin = 100,
  XtraBlack = 800,
  Italic = "italic",
}

const fontFamilyCss = (fontFamily: FontFamily) => {
  switch (fontFamily) {
    case FontFamily.Primary:
      return css`
        [lang="en"] & {
          font-family: ${TYPOGRAPHY.en.primary};
        }
        &[lang="en"] {
          font-family: ${TYPOGRAPHY.en.primary} !important;
        }

        [lang="ka"] & {
          font-family: ${TYPOGRAPHY.ka.primary};
        }
        &[lang="ka"] {
          font-family: ${TYPOGRAPHY.ka.primary} !important;
        }

        [lang="ru"] & {
          font-family: ${TYPOGRAPHY.ru.primary};
        }
        &[lang="ru"] {
          font-family: ${TYPOGRAPHY.ru.primary} !important;
        }
      `;
    case FontFamily.Secondary:
      return css`
        [lang="en"] & {
          font-family: ${TYPOGRAPHY.en.secondary};
        }
        &[lang="en"] {
          font-family: ${TYPOGRAPHY.en.secondary} !important;
        }
        [lang="ka"] & {
          font-family: ${TYPOGRAPHY.ka.secondary};
        }
        &[lang="ka"] {
          font-family: ${TYPOGRAPHY.ka.secondary} !important;
        }
        [lang="ru"] & {
          font-family: ${TYPOGRAPHY.ru.secondary};
        }
        &[lang="ru"] {
          font-family: ${TYPOGRAPHY.ru.secondary} !important;
        }
      `;
  }
};

const TextStyling = (
  fontSize: string,
  lineHeight: string,
  fontFamily: FontFamily,
  textStyle: TextStyle,
  color: string | undefined | null
) => {
  return css`
    font-size: ${fontSize};
    ${typeof textStyle === "number"
      ? `font-weight: ${textStyle};`
      : `font-style: ${textStyle};`}
    line-height: ${lineHeight};
    ${color === null ? "" : `color: ${color || COLORS.Black};`}
    ${fontFamilyCss(fontFamily)}
  `;
};

// Updated headline components using XtraBlack for SemiBold
const HeadlineXLStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "40px",
    "52px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );
const HeadlineLStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "32px",
    "40px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );
const HeadlineMStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "28px",
    "36px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );
const HeadlineSStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "24px",
    "32px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );
const HeadlineXSStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "20px",
    "24px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );
const HeadlineXXSStyling = (
  style?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "16px",
    "20px",
    family || FontFamily.Primary,
    style || TextStyle.XtraBlack,
    color
  );

// Component styles using Medium as default
const ComponentXLStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "24px",
    "28px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

const ComponentLStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "20px",
    "24px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

const ComponentMStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "18px",
    "24px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

const ComponentSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "16px",
    "20px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

const ComponentXSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "14px",
    "16px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

const ComponentXXSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "12px",
    "16px",
    family || FontFamily.Primary,
    textStyle || TextStyle.Medium,
    color
  );

// Paragraph styles
const ParagraphXLStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "24px",
    "36px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );
const ParagraphLStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "20px",
    "32px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );
const ParagraphMStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "18px",
    "28px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );
const ParagraphSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "16px",
    "20px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );
const ParagraphXSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "14px",
    "20px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );

const ParagraphXXSStyling = (
  textStyle?: TextStyle,
  color?: string | null,
  family?: FontFamily
) =>
  TextStyling(
    "12px",
    "16px",
    family || FontFamily.Secondary,
    textStyle || TextStyle.Regular,
    color
  );

export { TYPOGRAPHY };
export { TextStyle, FontFamily };
export {
  ComponentXLStyling,
  ComponentLStyling,
  ComponentMStyling,
  ComponentSStyling,
  ComponentXSStyling,
  ComponentXXSStyling,
};
export {
  ParagraphXLStyling,
  ParagraphLStyling,
  ParagraphMStyling,
  ParagraphSStyling,
  ParagraphXSStyling,
  ParagraphXXSStyling,
};
export {
  HeadlineXLStyling,
  HeadlineLStyling,
  HeadlineMStyling,
  HeadlineSStyling,
  HeadlineXSStyling,
  HeadlineXXSStyling,
};
